import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from 'src/lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

// The dialog surface colour. Callers with a different surface set `--dialog-bg` on
// DialogContent (e.g. className="[--dialog-bg:var(--color-bg-secondary)]") so the
// sticky header and footer bands match it.
const SURFACE = 'bg-[var(--dialog-bg,var(--color-bg-primary))]';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * Viewport-fit layout (2026-10-15). A dialog taller than the window used to push its
 * Cancel/Save row below the screen with no way to reach it (Operator edit on a laptop).
 * Now:
 *  - the Content element is a FRAME capped at the viewport (100dvh minus a margin)
 *    that never scrolls itself, so the close button stays put;
 *  - an inner BODY element scrolls, and DialogHeader / DialogFooter are sticky inside
 *    it, so the title and the action buttons are always on screen;
 *  - under 640px it becomes a full-width bottom sheet (up to the full screen height).
 *    Those phone rules are `!important` on purpose: callers widen dialogs with
 *    `w-[...] max-w-*` classes or an inline `maxWidth`/`width`, which must keep
 *    winning on a desktop but must not make a phone scroll sideways.
 * `className` and `style` still land on the frame, as before.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-[50%] top-[50%] z-50 flex max-h-[calc(100dvh-2rem)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-[var(--radius-card-lg)]',
        SURFACE,
        'max-sm:top-auto! max-sm:bottom-0! max-sm:left-0! max-sm:right-0! max-sm:w-full! max-sm:max-w-none! max-sm:max-h-[100dvh]! max-sm:translate-x-0! max-sm:translate-y-0! max-sm:rounded-t-[var(--radius-card-lg)] max-sm:border-x-0 max-sm:border-b-0',
        className,
      )}
      {...props}
    >
      <div
        data-dialog-body=""
        className="grid min-h-0 flex-1 gap-4 overflow-y-auto overscroll-contain p-6"
      >
        {children}
      </div>
      <DialogPrimitive.Close className="absolute right-1 top-1 z-20 flex h-14 w-14 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-5 w-5" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

// Sticky inside the dialog body. The negative margins cancel the body's p-6 so the
// band spans the full width and sits flush with the edge, and the surface colour
// hides content scrolling underneath. The offsets are -6, not 0, because a sticky
// box is held inside the scroller's padding: at 0 a 24px strip of scrolling content
// shows between the band and the edge. pr-14 keeps the title clear of the close button.
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => (
  <div
    className={cn(
      'sticky -top-6 z-10 -mx-6 -mt-6 flex flex-col space-y-1.5 px-6 pb-2 pr-14 pt-6 text-center sm:text-left',
      SURFACE,
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => (
  <div
    className={cn(
      'sticky -bottom-6 z-10 -mx-6 -mb-6 flex flex-col-reverse gap-2 border-t px-6 pb-6 pt-4 sm:flex-row sm:justify-end sm:gap-0 sm:space-x-2',
      SURFACE,
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
